import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Loader2, Save, Shield, Eye, EyeOff, History, Zap, CheckCircle2, Unlink, Youtube, ExternalLink } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const SocialMediaSettings = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);

  // Buffer-specific state
  const [bufferKey, setBufferKey] = useState('');
  const [showBufferKey, setShowBufferKey] = useState(false);
  const [savingBuffer, setSavingBuffer] = useState(false);
  const [bufferChannels, setBufferChannels] = useState([]);
  const [savingMapping, setSavingMapping] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showKeyRotation, setShowKeyRotation] = useState(false);

  // YouTube-specific state
  const [ytClientId, setYtClientId] = useState('');
  const [ytClientSecret, setYtClientSecret] = useState('');
  const [showYtSecret, setShowYtSecret] = useState(false);
  const [savingYt, setSavingYt] = useState(false);
  const [ytConnecting, setYtConnecting] = useState(false);
  const [ytDisconnecting, setYtDisconnecting] = useState(false);
  const [showYtRotation, setShowYtRotation] = useState(false);

  useEffect(() => { fetchSettings(); }, []);
  useEffect(() => {
    if (data.buffer_organization_id) {
      fetchBufferChannels();
    }
  }, [data.buffer_organization_id]);

  // Listen for OAuth popup completion
  useEffect(() => {
    const onMessage = (event) => {
      if (event.data?.type === 'youtube_oauth') {
        setYtConnecting(false);
        if (event.data.success) {
          toast.success(event.data.message || 'YouTube gekoppel.');
        } else {
          toast.error(event.data.message || 'YouTube koppeling het misluk.');
        }
        fetchSettings();
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const fetchBufferChannels = async () => {
    try {
      const r = await axios.get(`${API}/social-media/buffer/channels`);
      setBufferChannels(r.data.channels || []);
    } catch (e) {
      // Usually means the Buffer API key was just removed or never configured.
      // Log the underlying reason for debugging without surfacing a toast to the user.
      console.debug('[buffer-channels] silent failure:', e?.response?.status, e?.message);
    }
  };

  const handleConfigureBuffer = async () => {
    if (!bufferKey.trim()) {
      toast.error('Plak jou Buffer Personal API Key.');
      return;
    }
    setSavingBuffer(true);
    try {
      const r = await axios.post(`${API}/social-media/buffer/configure`, { api_key: bufferKey.trim() });
      toast.success(`Buffer gekoppel: ${r.data.organizations?.[0]?.name || 'OK'} — ${r.data.channels?.length || 0} kanale gevind.`);
      setBufferKey('');
      setShowKeyRotation(false);
      setBufferChannels(r.data.channels || []);
      fetchSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Buffer konfigurasie het misluk.');
    } finally {
      setSavingBuffer(false);
    }
  };

  const handleSaveBufferMapping = async () => {
    setSavingMapping(true);
    try {
      await axios.put(`${API}/social-media/buffer/mapping`, {
        buffer_facebook_channel_id: data.buffer_facebook_channel_id || '',
        buffer_facebook_channel_label: data.buffer_facebook_channel_label || '',
        buffer_instagram_channel_id: data.buffer_instagram_channel_id || '',
        buffer_instagram_channel_label: data.buffer_instagram_channel_label || '',
        buffer_tiktok_channel_id: data.buffer_tiktok_channel_id || '',
        buffer_tiktok_channel_label: data.buffer_tiktok_channel_label || '',
      });
      toast.success('Buffer kanaal afbeeldings gestoor.');
      fetchSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kon nie stoor nie.');
    } finally {
      setSavingMapping(false);
    }
  };

  const handleDisconnectBuffer = async () => {
    if (!window.confirm('Ontkoppel Buffer? (Dit verwyder slegs jou sleutel hier — jou Buffer rekening bly aktief.)')) return;
    setDisconnecting(true);
    try {
      await axios.delete(`${API}/social-media/buffer/configure`);
      toast.success('Buffer ontkoppel.');
      setBufferChannels([]);
      fetchSettings();
    } catch (e) {
      toast.error('Kon nie ontkoppel nie.');
    } finally {
      setDisconnecting(false);
    }
  };

  // ----- YouTube handlers -----
  const handleSaveYtCredentials = async () => {
    if (!ytClientId.trim() || !ytClientSecret.trim()) {
      toast.error('Plak beide jou Google Cloud Client ID en Client Secret.');
      return;
    }
    setSavingYt(true);
    try {
      await axios.post(`${API}/social-media/youtube/configure`, {
        client_id: ytClientId.trim(),
        client_secret: ytClientSecret.trim(),
      });
      toast.success('Google Cloud sleutels gestoor. Klik nou "Skakel met YouTube" om die kanaal te koppel.');
      setYtClientId('');
      setYtClientSecret('');
      setShowYtRotation(false);
      fetchSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kon nie stoor nie.');
    } finally {
      setSavingYt(false);
    }
  };

  const handleConnectYouTube = async () => {
    setYtConnecting(true);
    try {
      const r = await axios.get(`${API}/social-media/youtube/oauth-url`);
      const w = window.open(r.data.auth_url, 'youtube_oauth',
        'width=600,height=720,scrollbars=yes,resizable=yes');
      if (!w) {
        setYtConnecting(false);
        toast.error('Jou blaaier het die popup geblok. Laat popups toe en probeer weer.');
        return;
      }
      // The postMessage listener will flip ytConnecting back to false.
      // Also handle the case where the user closes the popup without finishing.
      const checkClosed = setInterval(() => {
        if (w.closed) {
          clearInterval(checkClosed);
          setTimeout(() => setYtConnecting(false), 500);
        }
      }, 500);
    } catch (e) {
      setYtConnecting(false);
      toast.error(e.response?.data?.detail || 'Kon nie OAuth begin nie.');
    }
  };

  const handleDisconnectYouTube = async () => {
    if (!window.confirm('Ontkoppel YouTube? Jy sal weer moet outoriseer om te plaas.')) return;
    setYtDisconnecting(true);
    try {
      await axios.delete(`${API}/social-media/youtube/configure`);
      toast.success('YouTube ontkoppel.');
      fetchSettings();
    } catch (e) {
      toast.error('Kon nie ontkoppel nie.');
    } finally {
      setYtDisconnecting(false);
    }
  };

  const channelByService = (svc) => bufferChannels.filter(c => {
    const s = `${c.service || ''} ${c.serviceName || ''}`.toLowerCase();
    if (svc === 'tiktok') return s.includes('tiktok') || s.includes('tik tok');
    return s.includes(svc);
  });

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/social-media/settings`);
      setData(r.data || {});
    } catch (e) {
      toast.error('Kon nie sosiale media instellings laai nie.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAudit = async () => {
    try {
      const r = await axios.get(`${API}/social-media/audit-log`);
      setAudit(r.data || []);
      setShowAudit(true);
    } catch (e) {
      toast.error('Kon nie ouditspoor laai nie.');
    }
  };

  if (loading) return <Card><CardContent className="p-6"><Loader2 className="h-5 w-5 animate-spin" /></CardContent></Card>;

  return (
    <Card className="border-orange-200" data-testid="social-media-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-orange-700">
          <Shield className="h-5 w-5" /> Sosiale Media Integrasie
        </CardTitle>
        <CardDescription>
          Slegs Hoof Admin. Tokens word geënkripteer gestoor en nooit aan die voorkant ontbloot nie.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* ===== Buffer (recommended) ===== */}
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-3"
          data-testid="buffer-section">
          <div className="flex items-center gap-2 font-semibold text-emerald-800">
            <Zap className="h-5 w-5" /> Buffer Integrasie (Aanbeveel)
          </div>
          <p className="text-xs text-emerald-900/80">
            Buffer hanteer alle Meta Developer toestemmings vir jou. Genereer ’n Personal API Key
            by{' '}
            <a className="underline" target="_blank" rel="noreferrer"
              href="https://publish.buffer.com/settings/api">publish.buffer.com/settings/api</a>
            {' '}en plak dit hieronder. Maak seker jou Facebook Page en Instagram Business rekening
            is reeds binne Buffer gekoppel.
          </p>

          {data.buffer_organization_id ? (
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                  Gekoppel aan: <strong>{data.buffer_organization_name || data.buffer_organization_id}</strong>
                  <span className="text-gray-600">({bufferChannels.length} kanale)</span>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={fetchBufferChannels}
                  data-testid="buffer-refresh-btn">
                  Verfris Kanale
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowKeyRotation(!showKeyRotation)}
                  data-testid="buffer-rotate-key-btn">
                  {showKeyRotation ? 'Kanselleer' : 'Werk API Sleutel by'}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={handleDisconnectBuffer}
                  disabled={disconnecting} className="text-red-600" data-testid="buffer-disconnect-btn">
                  <Unlink className="h-3 w-3 mr-1" /> Ontkoppel
                </Button>
              </div>

              {showKeyRotation && (
                <div className="rounded-md border border-emerald-300 bg-white p-3 space-y-2">
                  <Label>Nuwe Buffer Personal API Key</Label>
                  <p className="text-xs text-gray-600">
                    Plak ’n nuwe sleutel (bv. nadat die huidige een verval het). Kanaal afbeeldings
                    bly behoue — slegs die sleutel word vervang.
                  </p>
                  <div className="flex gap-2">
                    <Input
                      type={showBufferKey ? 'text' : 'password'}
                      value={bufferKey}
                      onChange={(e) => setBufferKey(e.target.value)}
                      placeholder="Plak die nuwe Buffer API key"
                      data-testid="buffer-rotate-input"
                    />
                    <Button type="button" variant="outline" size="icon"
                      onClick={() => setShowBufferKey(!showBufferKey)}>
                      {showBufferKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button type="button" onClick={handleConfigureBuffer} disabled={savingBuffer}
                      className="bg-emerald-600 hover:bg-emerald-700" data-testid="buffer-rotate-save-btn">
                      {savingBuffer ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Toets & Vervang
                    </Button>
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>Facebook → Buffer kanaal</Label>
                  <Select
                    value={data.buffer_facebook_channel_id || ''}
                    onValueChange={(v) => {
                      const chan = bufferChannels.find(c => c.id === v);
                      setData({
                        ...data,
                        buffer_facebook_channel_id: v,
                        buffer_facebook_channel_label: chan ? (chan.name || chan.username || chan.id) : '',
                      });
                    }}
                  >
                    <SelectTrigger data-testid="buffer-fb-select">
                      <SelectValue placeholder="Kies ’n Facebook kanaal" />
                    </SelectTrigger>
                    <SelectContent>
                      {channelByService('facebook').length === 0
                        ? <SelectItem value="__none__" disabled>Geen Facebook kanaal in Buffer nie</SelectItem>
                        : channelByService('facebook').map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {(c.name || c.username || c.id)} — {c.service || c.serviceName}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Instagram → Buffer kanaal</Label>
                  <Select
                    value={data.buffer_instagram_channel_id || ''}
                    onValueChange={(v) => {
                      const chan = bufferChannels.find(c => c.id === v);
                      setData({
                        ...data,
                        buffer_instagram_channel_id: v,
                        buffer_instagram_channel_label: chan ? (chan.name || chan.username || chan.id) : '',
                      });
                    }}
                  >
                    <SelectTrigger data-testid="buffer-ig-select">
                      <SelectValue placeholder="Kies ’n Instagram kanaal" />
                    </SelectTrigger>
                    <SelectContent>
                      {channelByService('instagram').length === 0
                        ? <SelectItem value="__none__" disabled>Geen Instagram kanaal in Buffer nie</SelectItem>
                        : channelByService('instagram').map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {(c.name || c.username || c.id)} — {c.service || c.serviceName}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>TikTok → Buffer kanaal</Label>
                  <Select
                    value={data.buffer_tiktok_channel_id || ''}
                    onValueChange={(v) => {
                      const chan = bufferChannels.find(c => c.id === v);
                      setData({
                        ...data,
                        buffer_tiktok_channel_id: v,
                        buffer_tiktok_channel_label: chan ? (chan.name || chan.username || chan.id) : '',
                      });
                    }}
                  >
                    <SelectTrigger data-testid="buffer-tiktok-select">
                      <SelectValue placeholder="Kies ’n TikTok kanaal" />
                    </SelectTrigger>
                    <SelectContent>
                      {channelByService('tiktok').length === 0
                        ? <SelectItem value="__none__" disabled>Geen TikTok kanaal in Buffer nie</SelectItem>
                        : channelByService('tiktok').map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {(c.name || c.username || c.id)} — {c.service || c.serviceName}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button type="button" onClick={handleSaveBufferMapping} disabled={savingMapping}
                className="bg-emerald-600 hover:bg-emerald-700" data-testid="buffer-save-mapping-btn">
                {savingMapping ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Stoor Kanaal Afbeeldings
              </Button>

              <p className="text-xs text-gray-600">
                Sodra jy ’n kanaal kies en stoor, sal advertensies vir daardie platform deur
                Buffer geplaas word.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Buffer Personal API Key</Label>
              <div className="flex gap-2">
                <Input
                  type={showBufferKey ? 'text' : 'password'}
                  value={bufferKey}
                  onChange={(e) => setBufferKey(e.target.value)}
                  placeholder="Plak jou Buffer API key hier"
                  data-testid="buffer-api-key-input"
                />
                <Button type="button" variant="outline" size="icon"
                  onClick={() => setShowBufferKey(!showBufferKey)}>
                  {showBufferKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button type="button" onClick={handleConfigureBuffer} disabled={savingBuffer}
                  className="bg-emerald-600 hover:bg-emerald-700" data-testid="buffer-configure-btn">
                  {savingBuffer ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Toets & Koppel
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* All direct Meta / IG / X token sections removed — Buffer handles posting. */}

        {/* ===== YouTube (direct API, bypasses Buffer's 3-channel limit) ===== */}
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-3"
          data-testid="youtube-section">
          <div className="flex items-center gap-2 font-semibold text-red-800">
            <Youtube className="h-5 w-5" /> YouTube Integrasie (Direkte API)
          </div>
          <p className="text-xs text-red-900/80">
            Plaas video&apos;s regstreeks na jou YouTube kanaal sonder om vir Buffer Pro op te gradeer.
            Jy benodig <strong>Google Cloud OAuth Client ID + Secret</strong> wat jy by{' '}
            <a className="underline" target="_blank" rel="noreferrer"
              href="https://console.cloud.google.com/apis/credentials">
              console.cloud.google.com/apis/credentials <ExternalLink className="inline h-3 w-3" />
            </a>
            {' '}skep. Volg die stap-vir-stap gids wat die agent vir jou gestuur het.
          </p>

          {data.youtube_channel_id ? (
            // ----- Already fully connected -----
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-red-700" />
                  Gekoppel aan: <strong>{data.youtube_channel_title}</strong>
                </div>
                <a href={`https://www.youtube.com/channel/${data.youtube_channel_id}`}
                  target="_blank" rel="noreferrer"
                  className="text-xs text-red-700 underline">
                  Bekyk kanaal <ExternalLink className="inline h-3 w-3" />
                </a>
                <Button type="button" variant="outline" size="sm"
                  onClick={() => setShowYtRotation(!showYtRotation)}
                  data-testid="youtube-rotate-key-btn">
                  {showYtRotation ? 'Kanselleer' : 'Vervang Client ID/Secret'}
                </Button>
                <Button type="button" variant="outline" size="sm"
                  onClick={handleDisconnectYouTube}
                  disabled={ytDisconnecting} className="text-red-600"
                  data-testid="youtube-disconnect-btn">
                  <Unlink className="h-3 w-3 mr-1" /> Ontkoppel
                </Button>
              </div>

              {showYtRotation && (
                <div className="rounded-md border border-red-300 bg-white p-3 space-y-2">
                  <Label>Nuwe Google Cloud OAuth Sleutels</Label>
                  <p className="text-xs text-gray-600">
                    Slegs nodig as jy nuwe sleutels in Google Cloud geskep het. Die kanaal
                    sal weer outoriseer moet word na hierdie aksie.
                  </p>
                  <Input value={ytClientId}
                    onChange={(e) => setYtClientId(e.target.value)}
                    placeholder="Nuwe Client ID"
                    data-testid="youtube-rotate-client-id-input" />
                  <div className="flex gap-2">
                    <Input
                      type={showYtSecret ? 'text' : 'password'}
                      value={ytClientSecret}
                      onChange={(e) => setYtClientSecret(e.target.value)}
                      placeholder="Nuwe Client Secret"
                      data-testid="youtube-rotate-client-secret-input" />
                    <Button type="button" variant="outline" size="icon"
                      onClick={() => setShowYtSecret(!showYtSecret)}>
                      {showYtSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button type="button" onClick={handleSaveYtCredentials} disabled={savingYt}
                      className="bg-red-600 hover:bg-red-700"
                      data-testid="youtube-rotate-save-btn">
                      {savingYt ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Stoor & Vereis Heroutorisering
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : data.youtube_client_id_set ? (
            // ----- Credentials saved, but not yet OAuth'd -----
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-red-900">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                Client ID en Secret is gestoor ({data.youtube_client_id_masked}). Nou moet jy
                jou YouTube kanaal outoriseer.
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={handleConnectYouTube} disabled={ytConnecting}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="youtube-connect-btn">
                  {ytConnecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Youtube className="h-4 w-4 mr-2" />}
                  Skakel met YouTube
                </Button>
                <Button type="button" variant="outline"
                  onClick={() => setShowYtRotation(!showYtRotation)}
                  data-testid="youtube-rotate-key-btn">
                  {showYtRotation ? 'Kanselleer' : 'Vervang Sleutels'}
                </Button>
                <Button type="button" variant="outline" size="sm"
                  onClick={handleDisconnectYouTube}
                  disabled={ytDisconnecting} className="text-red-600"
                  data-testid="youtube-disconnect-btn">
                  <Unlink className="h-3 w-3 mr-1" /> Verwyder Sleutels
                </Button>
              </div>
              {showYtRotation && (
                <div className="rounded-md border border-red-300 bg-white p-3 space-y-2">
                  <Input value={ytClientId}
                    onChange={(e) => setYtClientId(e.target.value)}
                    placeholder="Nuwe Client ID"
                    data-testid="youtube-rotate-client-id-input" />
                  <div className="flex gap-2">
                    <Input
                      type={showYtSecret ? 'text' : 'password'}
                      value={ytClientSecret}
                      onChange={(e) => setYtClientSecret(e.target.value)}
                      placeholder="Nuwe Client Secret"
                      data-testid="youtube-rotate-client-secret-input" />
                    <Button type="button" variant="outline" size="icon"
                      onClick={() => setShowYtSecret(!showYtSecret)}>
                      {showYtSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button type="button" onClick={handleSaveYtCredentials} disabled={savingYt}
                      className="bg-red-600 hover:bg-red-700"
                      data-testid="youtube-rotate-save-btn">
                      {savingYt ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Stoor
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            // ----- Nothing yet — initial setup form -----
            <div className="space-y-2">
              <div>
                <Label>Google Cloud Client ID</Label>
                <Input value={ytClientId}
                  onChange={(e) => setYtClientId(e.target.value)}
                  placeholder="bv. 123456789-abcdefg.apps.googleusercontent.com"
                  data-testid="youtube-client-id-input" />
              </div>
              <div>
                <Label>Google Cloud Client Secret</Label>
                <div className="flex gap-2">
                  <Input
                    type={showYtSecret ? 'text' : 'password'}
                    value={ytClientSecret}
                    onChange={(e) => setYtClientSecret(e.target.value)}
                    placeholder="bv. GOCSPX-xxxxxxxxxxxxxxxxxxxxx"
                    data-testid="youtube-client-secret-input" />
                  <Button type="button" variant="outline" size="icon"
                    onClick={() => setShowYtSecret(!showYtSecret)}>
                    {showYtSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button type="button" onClick={handleSaveYtCredentials} disabled={savingYt}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="youtube-save-credentials-btn">
                    {savingYt ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Stoor Sleutels
                  </Button>
                </div>
              </div>
              <p className="text-xs text-gray-600">
                Na stoor verskyn ’n <strong>&quot;Skakel met YouTube&quot;</strong> knoppie wat ’n Google
                outorisasie venster sal open.
              </p>
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-2">
          <Button variant="outline" onClick={fetchAudit} data-testid="sm-audit-btn">
            <History className="h-4 w-4 mr-2" /> Bekyk Ouditspoor
          </Button>
        </div>

        {showAudit && (
          <div className="border-t pt-3">
            <h4 className="font-semibold mb-2">Ouditspoor (laaste 100 veranderinge)</h4>
            {audit.length === 0 ? <p className="text-sm text-gray-500">Geen rekord nie.</p> : (
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {audit.map((a, idx) => (
                  <div key={idx} className="text-xs border rounded p-2 bg-gray-50">
                    <p><strong>{a.user_email}</strong> · {a.action} ·{' '}
                      {a.timestamp ? new Date(a.timestamp).toLocaleString('af-ZA') : ''}</p>
                    {a.details?.changed_fields?.length > 0 && (
                      <p className="text-gray-600">Velde: {a.details.changed_fields.join(', ')}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SocialMediaSettings;
